import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Trash2, Mail, KeyRound, Plus, X, Check, Ticket, Copy, Eye, AlertTriangle, LockKeyhole } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { Dica } from "@/components/Dica";
import BackupCard from "@/components/BackupCard";
import ProxyDjenCard from "@/components/ProxyDjenCard";
import CalendariosProtegidosCard from "@/components/CalendariosProtegidosCard";
const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

const EVENTO_TXT = {
  oab_bloqueada: "Tentou mexer na OAB (travada)",
  cpf_duplicado: "CPF já usado em outra conta",
  oab_duplicada: "OAB já usada em outra conta",
  convite_criado: "Convite criado",
  convite_revogado: "Convite revogado",
};

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "—";
  }
};

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [fiscal, setFiscal] = useState(null);
  const [convites, setConvites] = useState([]);
  const [novo, setNovo] = useState({ code: "", label: "", max_uses: 1, expires_days: 30, email_restrito: "" });
  const [grant, setGrant] = useState(null); // { user, numero, uf }
  const [linkSenha, setLinkSenha] = useState(null); // { user, url, horas } — Lote 144

  const load = () => http.get("/admin/users").then((r) => setUsers(r.data));
  const loadExtra = () => {
    http.get("/admin/oab-solicitacoes", { params: { status: "pending" } }).then((r) => setPedidos(r.data)).catch(() => {});
    http.get("/admin/fiscalizacao").then((r) => setFiscal(r.data)).catch(() => {});
    http.get("/admin/convites").then((r) => setConvites(r.data)).catch(() => {});
  };
  useEffect(() => { load(); loadExtra(); }, []);

  const decidir = async (p, acao) => {
    let motivo = null;
    if (acao === "recusar") motivo = window.prompt("Motivo da recusa (opcional):") ?? null;
    try {
      await http.post(`/admin/oab-solicitacoes/${p.solicitacao_id}/${acao === "aprovar" ? "aprovar" : "recusar"}`, acao === "recusar" ? { motivo } : {});
      toast.success(acao === "aprovar" ? "Pedido aprovado" : "Pedido recusado");
      loadExtra(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao decidir"); }
  };

  const criarConvite = async () => {
    try {
      const body = {
        code: novo.code.trim() || null,
        label: novo.label.trim(),
        max_uses: Number(novo.max_uses) || 1,
        expires_days: novo.expires_days === "" ? null : Number(novo.expires_days),
        email_restrito: novo.email_restrito.trim() || null,
      };
      const r = await http.post("/admin/convites", body);
      toast.success(`Convite ${r.data.code} criado`);
      setNovo({ code: "", label: "", max_uses: 1, expires_days: 30, email_restrito: "" });
      loadExtra();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Não foi possível criar o convite");
    }
  };
  const revogarConvite = async (c) => {
    await http.post(`/admin/convites/${c.code}/revogar`);
    toast.info(`Convite ${c.code} revogado`);
    loadExtra();
  };
  const copiar = (txt) => { try { navigator.clipboard.writeText(txt); toast.success("Copiado"); } catch { /* ignora */ } };

  const conceder = async () => {
    try {
      const r = await http.post(`/admin/users/${grant.user.user_id}/oab`, { numero: grant.numero, uf: grant.uf });
      toast.success(r.data.oab_em_outra_conta ? "OAB incluída (atenção: já consta em outra conta)" : "OAB incluída");
      setGrant(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao incluir OAB"); }
  };
  const removerOab = async (u, o) => {
    try {
      await http.delete(`/admin/users/${u.user_id}/oab/${o.oab_id}`);
      toast.info(`OAB ${o.label} removida`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao remover OAB"); }
  };

  // Guarda no front (além da checagem no backend): quem não é o dono do sistema nem
  // deveria enxergar essa tela — se chegar aqui de algum jeito (link direto, etc.),
  // manda de volta pro Painel em vez de deixar a tela quebrar tentando carregar dados
  // que o servidor vai recusar com 403.
  if (user && !user.is_owner) {
    return <Navigate to="/dashboard" replace />;
  }

  // Lote 144 — link de nova senha gerado pelo administrador (24 h, uso único), para entregar à pessoa.
  const gerarLinkSenha = async (u) => {
    try {
      const r = await http.post(`/admin/users/${u.user_id}/link-senha`);
      setLinkSenha({ user: u, url: r.data.url, horas: r.data.expires_hours });
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao gerar o link"); }
  };

  const handleDelete = async (u) => {
    setDeletingId(u.user_id);
    try {
      await http.delete(`/admin/users/${u.user_id}`);
      toast.success(`Conta de ${u.name || u.email} excluída`);
      setUsers((cur) => cur.filter((x) => x.user_id !== u.user_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir conta");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-6xl mx-auto pb-24 md:pb-8">
      <header className="mb-6">
        <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-emerald-brand mb-2 flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.25} />
          Administração
        </div>
        <h1 className="font-display text-3xl font-bold text-stone-900">Contas cadastradas</h1>
        <Dica className="text-stone-500 mt-2 text-sm max-w-2xl">{`Visível só pra sua conta. Aqui você vê quem já se cadastrou no IApauta e pode excluir uma conta (útil pra limpar contas de teste) — a exclusão remove também todos os dados dessa conta (clientes, audiências, prazos, publicações).`}</Dica>
      </header>

      {/* ===== Backup (Lote 124) ===== */}
      <BackupCard />
      <ProxyDjenCard />
      <CalendariosProtegidosCard />

      {/* ===== Pedidos de OAB pendentes ===== */}
      <section data-testid="admin-pedidos-oab" className="bg-white border border-stone-200 rounded-md p-5 mb-6">
        <h2 className="font-display text-lg font-semibold text-stone-900 mb-1">Pedidos de OAB</h2>
        <p className="text-xs text-stone-500 mb-3">Inclusão ou remoção de OAB pedida pelos usuários. Aprovar libera acima do limite de 2 da fase de teste.</p>
        {pedidos.length === 0 ? (
          <p className="text-sm text-stone-500">Nenhum pedido pendente.</p>
        ) : (
          <ul className="divide-y divide-stone-100">
            {pedidos.map((p) => (
              <li key={p.solicitacao_id} data-testid={`pedido-${p.solicitacao_id}`} className="py-3 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[240px]">
                  <div className="text-sm text-stone-900">
                    <strong>{p.user_name || p.user_email}</strong> pede para {p.tipo === "incluir" ? "incluir" : "remover"} a OAB{" "}
                    <span className="tabular-nums font-medium">{p.numero}/{p.uf}</span>
                  </div>
                  <div className="text-xs text-stone-500">Motivo: {p.motivo}</div>
                  {p.oab_em_outra_conta && (
                    <div className="text-xs text-amber-700 mt-0.5 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Essa OAB já consta em outra conta.
                    </div>
                  )}
                </div>
                <Button data-testid={`pedido-aprovar-${p.solicitacao_id}`} size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => decidir(p, "aprovar")}>
                  <Check className="w-3.5 h-3.5" /> Aprovar
                </Button>
                <Button data-testid={`pedido-recusar-${p.solicitacao_id}`} size="sm" variant="outline" className="gap-1.5" onClick={() => decidir(p, "recusar")}>
                  <X className="w-3.5 h-3.5" /> Recusar
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ===== Convites personalizados ===== */}
      <section data-testid="admin-convites" className="bg-white border border-stone-200 rounded-md p-5 mb-6">
        <h2 className="font-display text-lg font-semibold text-stone-900 mb-1 flex items-center gap-2">
          <Ticket className="w-4 h-4 text-emerald-brand" strokeWidth={2} /> Convites de acesso
        </h2>
        <Dica className="text-xs text-stone-500 mb-4 max-w-2xl">{`Crie um código por pessoa (ou por grupo). O código padrão que você já usa continua valendo normalmente; estes são um acréscimo, e dá pra limitar usos, validade e até o e-mail que pode usá-lo. Vale para criar conta tanto com e-mail e senha quanto pelo Google (quem já tem conta entra sem código).`}</Dica>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end mb-4">
          <div className="col-span-2">
            <Label className="text-xs text-stone-500 mb-1 block">Código (vazio = gera um)</Label>
            <Input data-testid="conv-code" placeholder="Ex: FABIO-2026" value={novo.code} onChange={(e) => setNovo({ ...novo, code: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-stone-500 mb-1 block">Para quem (anotação)</Label>
            <Input data-testid="conv-label" placeholder="Ex: Dr. Fabio" value={novo.label} onChange={(e) => setNovo({ ...novo, label: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs text-stone-500 mb-1 block">Nº de usos</Label>
            <Input data-testid="conv-uses" type="number" min={1} max={500} value={novo.max_uses} onChange={(e) => setNovo({ ...novo, max_uses: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs text-stone-500 mb-1 block">Validade (dias)</Label>
            <Input data-testid="conv-days" type="number" min={1} max={365} placeholder="sem prazo" value={novo.expires_days} onChange={(e) => setNovo({ ...novo, expires_days: e.target.value })} />
          </div>
          <div className="col-span-2 md:col-span-4">
            <Label className="text-xs text-stone-500 mb-1 block">Só este e-mail pode usar (opcional)</Label>
            <Input data-testid="conv-email" type="email" placeholder="pessoa@exemplo.com" value={novo.email_restrito} onChange={(e) => setNovo({ ...novo, email_restrito: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Button data-testid="conv-criar" onClick={criarConvite} className="w-full gap-2 bg-stone-900 hover:bg-stone-800 text-white">
              <Plus className="w-4 h-4" /> Criar convite
            </Button>
          </div>
        </div>
        {convites.length === 0 ? (
          <p className="text-sm text-stone-500">Nenhum convite personalizado criado ainda.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead><TableHead>Para</TableHead><TableHead>Usos</TableHead>
                  <TableHead>Validade</TableHead><TableHead>Situação</TableHead><TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {convites.map((c) => {
                  const ativo = c.active !== false && !c.expirado && !c.esgotado;
                  return (
                    <TableRow key={c.code} data-testid={`convite-${c.code}`}>
                      <TableCell className="font-mono text-sm">{c.code}</TableCell>
                      <TableCell className="text-sm">
                        {c.label || "—"}
                        {c.email_restrito && <div className="text-xs text-stone-500">{c.email_restrito}</div>}
                        {c.used_by?.length > 0 && <div className="text-xs text-stone-400">usado por {c.used_by.map((x) => x.email).join(", ")}</div>}
                      </TableCell>
                      <TableCell className="tabular-nums text-sm">{c.uses}/{c.max_uses}</TableCell>
                      <TableCell className="text-sm text-stone-500 tabular-nums">{c.expires_at ? formatDate(c.expires_at) : "sem prazo"}</TableCell>
                      <TableCell>
                        <Badge className={`border ${ativo ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-stone-100 text-stone-500 border-stone-200"}`}>
                          {c.active === false ? "Revogado" : c.expirado ? "Expirado" : c.esgotado ? "Esgotado" : "Ativo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" title="Copiar código" onClick={() => copiar(c.code)}><Copy className="w-3.5 h-3.5" /></Button>
                        {c.active !== false && (
                          <Button variant="ghost" size="sm" className="text-rose-700" data-testid={`convite-revogar-${c.code}`} onClick={() => revogarConvite(c)}>Revogar</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {/* ===== Fiscalização ===== */}
      <section data-testid="admin-fiscalizacao" className="bg-white border border-stone-200 rounded-md p-5 mb-6">
        <h2 className="font-display text-lg font-semibold text-stone-900 mb-1 flex items-center gap-2">
          <Eye className="w-4 h-4 text-emerald-brand" strokeWidth={2} /> Fiscalização
        </h2>
        <p className="text-xs text-stone-500 mb-3 max-w-2xl">
          Alertas para você avaliar — nada é bloqueado automaticamente. “Possível login compartilhado” aparece quando a mesma conta
          entra de 3 ou mais IPs e 2 ou mais tipos de aparelho em {fiscal?.janela_horas || 24} h (pode ser só troca de rede).
        </p>
        <h3 className="text-sm font-semibold text-stone-800 mb-1.5">Possível login compartilhado</h3>
        {!fiscal || fiscal.suspeitos.length === 0 ? (
          <p className="text-sm text-stone-500 mb-4" data-testid="fiscal-sem-suspeitos">Nenhuma conta suspeita nas últimas {fiscal?.janela_horas || 24} h.</p>
        ) : (
          <ul className="mb-4 space-y-2">
            {fiscal.suspeitos.map((x) => (
              <li key={x.user_id} data-testid={`suspeito-${x.user_id}`} className="text-sm border border-amber-200 bg-amber-50 rounded-md p-3">
                <strong>{x.name || x.email}</strong> — {x.entradas} entradas, {x.ips.length} IPs, aparelhos: {x.aparelhos.join(" / ")}
                <div className="text-xs text-stone-500">Última: {formatDateTime(x.ultima)}</div>
              </li>
            ))}
          </ul>
        )}
        <h3 className="text-sm font-semibold text-stone-800 mb-1.5">Ações registradas</h3>
        {!fiscal || fiscal.eventos.length === 0 ? (
          <p className="text-sm text-stone-500">Nenhuma ação registrada.</p>
        ) : (
          <ul className="divide-y divide-stone-100 max-h-72 overflow-y-auto">
            {fiscal.eventos.map((e) => (
              <li key={e.event_id} className="py-2 text-sm flex flex-wrap gap-x-3 gap-y-0.5">
                <span className="text-xs text-stone-400 tabular-nums">{formatDateTime(e.created_at)}</span>
                <span className="font-medium text-stone-800">{EVENTO_TXT[e.tipo] || e.tipo}</span>
                <span className="text-stone-600">{e.user_name || e.user_email}</span>
                <span className="text-xs text-stone-500">{e.detalhe}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {users === null ? (
        <div className="p-12 text-center text-stone-500">Carregando…</div>
      ) : users.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-md p-12 text-center text-stone-500">
          Nenhuma conta cadastrada ainda.
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Conta</TableHead>
                <TableHead>Login</TableHead>
                <TableHead>CPF / Termo</TableHead>
                <TableHead>OABs</TableHead>
                <TableHead>Criada em</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-xs font-semibold text-stone-500 shrink-0">
                        {(u.name || u.email || "?")[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-stone-900 truncate">{u.name || "—"}</div>
                        <div className="text-xs text-stone-500 truncate">{u.email}</div>
                      </div>
                      {u.is_owner && (
                        <Badge variant="outline" className="border-stone-300 text-stone-500 shrink-0">Você</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {u.has_google && (
                        <Badge className="bg-blue-100 text-blue-700 border border-blue-200 gap-1">
                          <Mail className="w-3 h-3" strokeWidth={2} /> Google
                        </Badge>
                      )}
                      {u.has_password && (
                        <Badge
                          className={`gap-1 border ${
                            u.email_verified
                              ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                              : "bg-amber-100 text-amber-700 border-amber-200"
                          }`}
                        >
                          <KeyRound className="w-3 h-3" strokeWidth={2} />
                          {u.email_verified ? "Senha" : "Senha (pendente)"}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-stone-600">
                    <div className="tabular-nums">{u.cpf_masked || <span className="text-amber-700">cadastro pendente</span>}</div>
                    <div className="text-stone-400">{u.terms_version ? `Termo v${u.terms_version} · ${formatDate(u.terms_accepted_at)}` : "sem aceite"}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1">
                      {(u.oabs_det || []).map((o) => (
                        <span key={o.oab_id} className="inline-flex items-center gap-1 text-xs tabular-nums bg-stone-100 rounded px-1.5 py-0.5">
                          {o.label}
                          {!u.is_owner && (
                            <button type="button" title="Remover OAB" data-testid={`admin-rm-oab-${o.oab_id}`} onClick={() => removerOab(u, o)} className="text-stone-400 hover:text-rose-600">
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </span>
                      ))}
                      <button type="button" data-testid={`admin-add-oab-${u.user_id}`} onClick={() => setGrant({ user: u, numero: "", uf: "SP" })}
                        className="text-xs text-emerald-brand font-semibold hover:underline flex items-center gap-0.5">
                        <Plus className="w-3 h-3" /> OAB
                      </button>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-stone-500 tabular-nums">{formatDate(u.created_at)}</TableCell>
                  <TableCell className="text-right">
                    {u.is_owner ? (
                      <span className="text-xs text-stone-400">—</span>
                    ) : (
                      <div className="flex flex-col items-end gap-1.5">
                      <Button
                        data-testid={`admin-link-senha-${u.user_id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => gerarLinkSenha(u)}
                        className="gap-1.5 border-stone-300 text-stone-700 hover:bg-stone-100"
                      >
                        <LockKeyhole className="w-3.5 h-3.5" strokeWidth={2} /> Nova senha
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            data-testid={`admin-delete-button-${u.user_id}`}
                            variant="outline"
                            size="sm"
                            disabled={deletingId === u.user_id}
                            className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-100"
                          >
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={2} /> Excluir
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir conta de {u.name || u.email}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Isso apaga a conta e todos os dados vinculados a ela (clientes,
                              audiências, prazos, publicações). Não tem como desfazer.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              data-testid={`admin-confirm-delete-${u.user_id}`}
                              onClick={() => handleDelete(u)}
                              className="bg-rose-600 hover:bg-rose-700"
                            >
                              Excluir de vez
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!linkSenha} onOpenChange={(o) => !o && setLinkSenha(null)}>
        <DialogContent data-testid="admin-link-senha-dialog">
          <DialogHeader><DialogTitle>Link de nova senha para {linkSenha?.user?.name || linkSenha?.user?.email}</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-600 leading-relaxed">
            Envie este link só para a própria pessoa (por WhatsApp, por exemplo). Ele vale por {linkSenha?.horas} horas e só funciona uma vez;
            ao usar, a pessoa escolhe a senha nova e as sessões antigas dela são encerradas. Gerar outro link invalida este.
          </p>
          <div className="flex items-center gap-2">
            <Input readOnly value={linkSenha?.url || ""} data-testid="admin-link-senha-url" className="text-xs" onFocus={(e) => e.target.select()} />
            <Button type="button" variant="outline" onClick={() => copiar(linkSenha?.url || "")} className="gap-1.5 shrink-0">
              <Copy className="w-3.5 h-3.5" /> Copiar
            </Button>
          </div>
          <DialogFooter><Button type="button" onClick={() => setLinkSenha(null)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!grant} onOpenChange={(o) => !o && setGrant(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Incluir OAB em {grant?.user?.name || grant?.user?.email}</DialogTitle></DialogHeader>
          <div className="flex gap-3">
            <div className="flex-1">
              <Label className="text-xs text-stone-500 mb-1 block">Número</Label>
              <Input data-testid="grant-numero" inputMode="numeric" value={grant?.numero || ""} onChange={(e) => setGrant({ ...grant, numero: e.target.value.replace(/\D/g, "") })} />
            </div>
            <div className="w-24">
              <Label className="text-xs text-stone-500 mb-1 block">UF</Label>
              <select data-testid="grant-uf" className="h-9 w-full rounded-md border border-stone-200 bg-white px-2 text-sm" value={grant?.uf || "SP"} onChange={(e) => setGrant({ ...grant, uf: e.target.value })}>
                {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGrant(null)}>Cancelar</Button>
            <Button data-testid="grant-confirmar" disabled={!grant?.numero} onClick={conceder} className="bg-stone-900 hover:bg-stone-800 text-white">Incluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
